import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Bot, UserCheck, ClipboardEdit, ShieldOff, RotateCcw, CalendarClock, UserPlus, Paperclip } from "lucide-react";
import { StatusChip } from "./SectionBars";
import EvidencePanel from "./EvidencePanel";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
const OPEN_STATUSES = new Set(["fail", "manual", "error"]);

/** One CIS control: summary row + expandable evidence / audit / remediation / attestation / owner / files. */
export default function ControlRow({ control: c, isAdmin, me, tenantId, focused, onAttest, onClear, onAssign, onFilesChange }) {
  const [open, setOpen] = useState(!!focused);
  const ref = useRef(null);
  useEffect(() => {
    if (focused) {
      setOpen(true);
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focused]);
  const a = c.attestation;
  const o = c.owner;
  const files = c.files || [];
  const canException = isAdmin && c.automated && c.result === "fail" && !a;
  const canRenew = isAdmin && c.automated && !!a;
  const canAttest = isAdmin && !c.automated;
  const canAssign = isAdmin && (OPEN_STATUSES.has(c.status) || !!o);
  const showEvidence = !c.automated || a?.status === "exception" || files.length > 0;
  return (
    <li ref={ref} className={`cis-row ${c.status} ${open ? "open" : ""} ${focused ? "focused" : ""}`} data-testid={`cis-control-${c.id}`}>
      <button type="button" className="cis-row-main" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`cis-control-toggle-${c.id}`}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="cis-id mono">{c.id}</span>
        <span className="cis-title">
          {c.title}
          <span className="cis-tags">
            <span className={`mini-chip level l${c.level}`}>L{c.level}</span>
            {c.profile === "E5" && <span className="mini-chip sec-suite">E5</span>}
            <span className="mini-chip" title={c.automated ? "Evaluated automatically from Microsoft Graph" : "Requires manual review and attestation"}>
              {c.automated ? <Bot size={10} /> : <UserCheck size={10} />} {c.automated ? "Automated" : "Manual"}
            </span>
            {o && (
              <span className={`mini-chip owner ${o.overdue ? "crit" : ""} ${o.user_id === me ? "mine" : ""}`} title={`${o.name} <${o.email}>${o.due_at ? ` · due ${o.due_at}` : ""}`} data-testid={`cis-owner-${c.id}`}>
                <UserCheck size={10} /> {o.user_id === me ? "You" : o.name}
                {o.due_at ? ` · due ${o.due_at}` : ""}
                {o.overdue ? " · overdue" : ""}
              </span>
            )}
            {files.length > 0 && (
              <span className="mini-chip" title={`${files.length} evidence file(s)`} data-testid={`cis-files-${c.id}`}>
                <Paperclip size={10} /> {files.length}
              </span>
            )}
          </span>
        </span>
        <StatusChip status={c.status} testId={`cis-status-${c.id}`} />
        {a?.overdue && (
          <span className="cis-status crit overdue" title={`Review was due ${a.review_at}`} data-testid={`cis-overdue-${c.id}`}>
            <CalendarClock size={10} /> Review overdue
          </span>
        )}
      </button>
      {open && (
        <div className="cis-row-detail" data-testid={`cis-control-detail-${c.id}`}>
          <div className="cis-detail-grid">
            <div>
              <dt>{c.automated ? "Result" : "Assessment"}</dt>
              <dd data-testid={`cis-evidence-${c.id}`}>{c.evidence}</dd>
              {a && (
                <p className={`cis-attest-note ${a.status}`} data-testid={`cis-attestation-${c.id}`}>
                  <ClipboardEdit size={12} /> <b>{a.status === "exception" ? "Accepted risk" : `Attested: ${a.status === "na" ? "not applicable" : a.status}`}</b>
                  {a.note ? ` — ${a.note}` : ""} <span className="muted small">({a.by_name}, {fmt(a.at)})</span>
                  <span className={`mini-chip ${a.overdue ? "crit" : ""}`} data-testid={`cis-review-${c.id}`}>
                    <CalendarClock size={10} /> review by {a.review_at || "—"}
                    {a.overdue ? " · overdue" : ""}
                  </span>
                </p>
              )}
              {o && (
                <p className={`cis-owner-note ${o.overdue ? "crit" : ""}`} data-testid={`cis-owner-detail-${c.id}`}>
                  <UserCheck size={12} /> <b>Owner:</b> {o.name} <span className="muted small">&lt;{o.email}&gt;</span>
                  {o.due_at ? ` · due ${o.due_at}${o.overdue ? " (overdue)" : ""}` : " · no due date"}
                  {o.note ? ` — ${o.note}` : ""} <span className="muted small">(assigned by {o.assigned_by_name}, {fmt(o.at)})</span>
                </p>
              )}
            </div>
            <div>
              <dt>Audit</dt>
              <dd>{c.audit}</dd>
            </div>
            <div>
              <dt>Remediation</dt>
              <dd>{c.remediation}</dd>
            </div>
          </div>
          {showEvidence && <EvidencePanel tenantId={tenantId} controlId={c.id} files={files} isAdmin={isAdmin} onChange={(next) => onFilesChange(c.id, next)} />}
          {isAdmin && (
            <div className="cis-row-actions">
              {canAttest && (
                <button type="button" className="tb-btn sm" onClick={() => onAttest(c, "attest")} data-testid={`cis-attest-${c.id}`}>
                  <ClipboardEdit size={12} /> {a ? "Update attestation" : "Attest"}
                </button>
              )}
              {canException && (
                <button type="button" className="tb-btn sm" onClick={() => onAttest(c, "exception")} data-testid={`cis-exception-${c.id}`}>
                  <ShieldOff size={12} /> Accept risk
                </button>
              )}
              {canRenew && (
                <button type="button" className="tb-btn sm" onClick={() => onAttest(c, "exception")} data-testid={`cis-review-renew-${c.id}`}>
                  <CalendarClock size={12} /> {a.overdue ? "Review now" : "Update / extend review"}
                </button>
              )}
              {canAssign && (
                <button type="button" className="tb-btn sm" onClick={() => onAssign(c)} data-testid={`cis-assign-${c.id}`}>
                  <UserPlus size={12} /> {o ? "Change owner" : "Assign owner"}
                </button>
              )}
              {a && (
                <button type="button" className="tb-btn sm ghost" onClick={() => onClear(c)} data-testid={`cis-clear-${c.id}`}>
                  <RotateCcw size={12} /> Clear {a.status === "exception" ? "exception" : "attestation"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
